import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AccountContextService } from '../services/account-context.service';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SUBSCRIPTION_LEVELS, SubscriptionLevel } from '../models/subscription-level.model';

@Injectable({ providedIn: 'root' })
export class SubscriptionGuard {
  constructor(
    private router: Router,
    private accountContextService: AccountContextService
  ) {}

  private levels = SUBSCRIPTION_LEVELS;

  hasMinimumLevel(required: SubscriptionLevel): Observable<boolean> {
    return this.accountContextService.activeSubscriptionProfile$.pipe(
      map(subscriptionProfile => this.evaluateSubscriptionAccess(subscriptionProfile, required)),
      tap(result => {
        if (!result.allowed) {
          this.router.navigate([result.redirectTo]);
        }
      }),
      map(result => result.allowed)
    );
  }

  private evaluateSubscriptionAccess(user: any, required: SubscriptionLevel): { allowed: boolean; redirectTo: string } {
    if (!user) {
      return { allowed: false, redirectTo: '/login' };
    }

    const isActive = this.isAccessActive(user);
    const currentLevel = this.resolveSubscriptionLevel(user, isActive);
    if (!currentLevel) {
      return {
        allowed: false,
        redirectTo: isActive || this.hasSubscriptionTimeline(user) ? '/upgrade' : '/subscriptions'
      };
    }

    const allowed = this.levels.indexOf(currentLevel) >= this.levels.indexOf(required);
    return { allowed, redirectTo: '/upgrade' };
  }

  private resolveSubscriptionLevel(user: any, isActive: boolean): SubscriptionLevel | null {
    const directLevel = this.normalizeSubscriptionLevel(user?.subscriptionLevel);
    if (directLevel) {
      return directLevel;
    }

    const legacyLevel = this.normalizeSubscriptionLevel(user?.planLevel);
    if (legacyLevel) {
      return legacyLevel;
    }

    if (isActive) {
      return 'starter';
    }

    return null;
  }

  private normalizeSubscriptionLevel(level?: string): SubscriptionLevel | null {
    if (!level) {
      return null;
    }

    return SUBSCRIPTION_LEVELS.includes(level as SubscriptionLevel) ? (level as SubscriptionLevel) : null;
  }

  private isAccessActive(user: any): boolean {
    const now = new Date();
    const trialEndsAt = this.toDate(user?.trialEndsAt);
    const currentPeriodEnd = this.toDate(user?.currentPeriodEnd);
    const expiresAt = this.toDate(user?.expiresAt);
    const paymentStatus = typeof user?.paymentStatus === 'string' ? user.paymentStatus.trim().toLowerCase() : '';

    if (trialEndsAt && now <= trialEndsAt) {
      return true;
    }

    if (currentPeriodEnd && now <= currentPeriodEnd) {
      return true;
    }

    if (expiresAt && now <= expiresAt) {
      return true;
    }

    if (paymentStatus === 'active') {
      return true;
    }

    return user?.isSubscriptionActive ?? false;
  }

  private hasSubscriptionTimeline(user: any): boolean {
    return !!(
      this.toDate(user?.trialEndsAt) ||
      this.toDate(user?.currentPeriodEnd) ||
      this.toDate(user?.expiresAt)
    );
  }

  private toDate(value?: any): Date | null {
    if (!value) {
      return null;
    }

    const date = value?.toDate ? value.toDate() : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
}
